import type * as React from "react"

import { isHeldForSigning } from "../../Esign/helpers/signable-record.ts"

const PROPOSAL_STATUSES = {
  DRAFT: "draft",
  PENDING_TO_REVIEW: "pending_review",
  APPROVED: "approved",
  DECLINED: "declined",
  CANCELLED: "cancelled",
  SENT_TO_CLIENT: "sent",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  EXPIRED: "expired"
} as const

type ProposalStatusesKeys = keyof typeof PROPOSAL_STATUSES
type ProposalStatuses = (typeof PROPOSAL_STATUSES)[ProposalStatusesKeys]

const PROPOSAL_STATUSES_OBJ: Record<
  string,
  {
    id: ProposalStatuses
    value: ProposalStatuses
    label: string
    style: React.CSSProperties
  }
> = {
  [PROPOSAL_STATUSES.DRAFT]: {
    id: PROPOSAL_STATUSES.DRAFT,
    value: PROPOSAL_STATUSES.DRAFT,
    label: "Draft",
    style: {
      color: "var(--dk-text-secondary, #616d73)",
      backgroundColor: "var(--dk-subtle, #ECEFF1)",
      borderColor: "var(--dk-subtle, #ECEFF1)"
    }
  },
  [PROPOSAL_STATUSES.PENDING_TO_REVIEW]: {
    id: PROPOSAL_STATUSES.PENDING_TO_REVIEW,
    value: PROPOSAL_STATUSES.PENDING_TO_REVIEW,
    label: "In Review",
    style: {
      color: "var(--dk-purple-text, #6e21f3)",
      backgroundColor: "var(--dk-purple-bg, #efe3fd)",
      borderColor: "var(--dk-purple-bg, #efe3fd)"
    }
  },
  [PROPOSAL_STATUSES.APPROVED]: {
    id: PROPOSAL_STATUSES.APPROVED,
    value: PROPOSAL_STATUSES.APPROVED,
    label: "Approved",
    style: {
      color: "var(--dk-success-text, #367B38)",
      backgroundColor: "var(--dk-success-bg, #E8F5E9)",
      borderColor: "var(--dk-success-bg, #E8F5E9)"
    }
  },
  [PROPOSAL_STATUSES.DECLINED]: {
    id: PROPOSAL_STATUSES.DECLINED,
    value: PROPOSAL_STATUSES.DECLINED,
    label: "Under Revision",
    style: {
      color: "var(--dk-warning-text, #9E5E00)",
      backgroundColor: "var(--dk-warning-bg, #FFF3E0)",
      borderColor: "var(--dk-warning-bg, #FFF3E0)"
    }
  },
  [PROPOSAL_STATUSES.CANCELLED]: {
    id: PROPOSAL_STATUSES.CANCELLED,
    value: PROPOSAL_STATUSES.CANCELLED,
    label: "Cancelled",
    style: {
      color: "var(--dk-error-text, #D31F1B)",
      backgroundColor: "var(--dk-error-bg, #FFEBEE)",
      borderColor: "var(--dk-error-bg, #FFEBEE)"
    }
  },
  [PROPOSAL_STATUSES.SENT_TO_CLIENT]: {
    id: PROPOSAL_STATUSES.SENT_TO_CLIENT,
    value: PROPOSAL_STATUSES.SENT_TO_CLIENT,
    label: "Sent to Client",
    style: {
      color: "var(--dk-primary-text, #007986)",
      backgroundColor: "var(--dk-selected, #E0F7FA)",
      borderColor: "var(--dk-selected, #E0F7FA)"
    }
  },
  [PROPOSAL_STATUSES.ACCEPTED]: {
    id: PROPOSAL_STATUSES.ACCEPTED,
    value: PROPOSAL_STATUSES.ACCEPTED,
    label: "Accepted",
    style: {
      color: "var(--dk-success-text, #296E2C)",
      backgroundColor: "var(--dk-success-bg, #C8E6C9)",
      borderColor: "var(--dk-success-bg, #C8E6C9)"
    }
  },
  [PROPOSAL_STATUSES.REJECTED]: {
    id: PROPOSAL_STATUSES.REJECTED,
    value: PROPOSAL_STATUSES.REJECTED,
    label: "Rejected",
    style: {
      color: "var(--dk-error-text, #B32424)",
      backgroundColor: "var(--dk-error-bg, #FFCDD2)",
      borderColor: "var(--dk-error-bg, #FFCDD2)"
    }
  },
  [PROPOSAL_STATUSES.EXPIRED]: {
    id: PROPOSAL_STATUSES.EXPIRED,
    value: PROPOSAL_STATUSES.EXPIRED,
    label: "Expired",
    style: {
      color: "var(--dk-text-secondary, #6F6F6F)",
      backgroundColor: "var(--dk-subtle, #F5F5F5)",
      borderColor: "var(--dk-subtle, #F5F5F5)"
    }
  }
}

const PROPOSAL_STATUSES_MAP = Object.fromEntries(
  Object.values(PROPOSAL_STATUSES_OBJ).map((item) => [item.label, item])
) as Record<
  string,
  (typeof PROPOSAL_STATUSES_OBJ)[keyof typeof PROPOSAL_STATUSES_OBJ]
>

const PROPOSAL_SHARE_EMAIL_STATUSES = [
  PROPOSAL_STATUSES.APPROVED,
  PROPOSAL_STATUSES.SENT_TO_CLIENT
] as ProposalStatuses[]

/** The server refuses to email a proposal while a signature request holds it. */
const canShareProposalByEmail = (proposal: {
  status?: string
  in_signing?: boolean
}) =>
  PROPOSAL_SHARE_EMAIL_STATUSES.includes(proposal.status as ProposalStatuses) &&
  !isHeldForSigning(proposal)

export {
  PROPOSAL_STATUSES,
  PROPOSAL_SHARE_EMAIL_STATUSES,
  canShareProposalByEmail,
  PROPOSAL_STATUSES_OBJ,
  PROPOSAL_STATUSES_MAP,
  type ProposalStatuses
}
